import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import PrintIcon from '@mui/icons-material/Print'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import axios from 'axios'

import { toast } from '../../../components/LsToast'
import { ReprintDialog } from '../../../components/ReprintDialog'
import { useEscapeKey } from '../../../hooks/useEscapeKey'
import { usePrinter } from '../../../hooks/usePrinter'
import { printReceipt } from '../../../service/print.service'
import { cancelReceipt } from '../../../service/receipts.service'
import { ReceiptDto, ReceiptStatus } from '../../../service/types'

export const DRAWER_WIDTH = { sm: 480, xl: 600 }

type Props = {
  open: boolean
  onClose: () => void
  receipt: ReceiptDto | null
  onReceiptUpdated: () => void
}

export const ReceiptDetailDrawer: FC<Props> = ({ open, onClose, receipt, onReceiptUpdated }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const { printerIp } = usePrinter()
  const [printing, setPrinting] = useState(false)
  const [reprintOpen, setReprintOpen] = useState(false)
  const [retrying, setRetrying] = useState(false)

  const handlePrint = async () => {
    if (!receipt || !printerIp) return
    setPrinting(true)
    try {
      await printReceipt(printerIp, receipt, t)
      toast.success(t('print.success'))
    } catch {
      setReprintOpen(true)
    } finally {
      setPrinting(false)
    }
  }

  const handleRetryPrint = async () => {
    if (!receipt || !printerIp) return
    setRetrying(true)
    try {
      await printReceipt(printerIp, receipt, t)
      toast.success(t('print.success'))
      setReprintOpen(false)
    } catch {
      toast.error(t('print.failed'))
    } finally {
      setRetrying(false)
    }
  }

  const handleCancel = async () => {
    if (!receipt) return
    setCancelling(true)
    try {
      await cancelReceipt(receipt.id)
      toast.success(t('receipts.detail.cancelledSuccessfully'))
      onReceiptUpdated()
      setCancelDialogOpen(false)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 503) {
        toast.error(t('receipts.detail.cancelTerminalOffline'))
      } else {
        toast.error(t('general.notification.unexpectedError'))
      }
    } finally {
      setCancelling(false)
    }
  }

  const totalBase = (receipt?.bookings ?? []).reduce((sum, b) => sum + Number(b.basePrice), 0)
  const totalVat = (receipt?.bookings ?? []).reduce((sum, b) => sum + Number(b.vat), 0)
  const totalPrice = (receipt?.bookings ?? []).reduce((sum, b) => sum + Number(b.price), 0)

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" noWrap sx={{ flex: 1 }}>
          {receipt ? t('receipts.detail.title', { number: receipt.receiptNumber }) : ''}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Action buttons */}
      {receipt && (
        <Box sx={{ display: 'flex', gap: 1, px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={printing || !printerIp}
            fullWidth
          >
            {t('print.receipt.print')}
          </Button>
          {receipt.status === ReceiptStatus.Paid && (
            <Button variant="outlined" size="large" color="error" onClick={() => setCancelDialogOpen(true)} fullWidth>
              {t('receipts.detail.cancelReceipt')}
            </Button>
          )}
        </Box>
      )}

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
        {receipt && (
          <>
            {/* Info section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label={t(`paymentType.${receipt.paymentType}`)} size="small" />
                <Chip
                  label={t(`receiptStatus.${receipt.status}`)}
                  size="small"
                  color={receipt.status === ReceiptStatus.Paid ? 'success' : 'error'}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {receipt.customer?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('receipts.detail.posId')}: {receipt.posId}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Bookings table */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t('receipts.detail.bookingsList')}
            </Typography>
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('receipts.detail.skierName')}</TableCell>
                    <TableCell align="right">{t('receipts.detail.price')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipt.bookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>{booking.skierName}</TableCell>
                      <TableCell align="right">{Number(booking.price).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            {/* Totals */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{t('receipts.detail.basePrice')}</Typography>
                <Typography variant="body2">{totalBase.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{t('receipts.detail.vat')}</Typography>
                <Typography variant="body2">{totalVat.toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {t('receipts.detail.total')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {totalPrice.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          </>
        )}
      </Box>

      {/* Cancel confirmation dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>{t('receipts.detail.cancelReceipt')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('receipts.detail.cancelConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>{t('general.button.cancel')}</Button>
          <Button onClick={handleCancel} color="error" disabled={cancelling}>
            {t('general.button.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )

  useEscapeKey(onClose, open)

  if (isSmallScreen) {
    return (
      <>
        <ReprintDialog
          open={reprintOpen}
          onCancel={() => setReprintOpen(false)}
          onRetry={handleRetryPrint}
          retrying={retrying}
        />
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: '100%' } }}>
          {drawerContent}
        </Drawer>
      </>
    )
  }

  return (
    <>
      <ReprintDialog
        open={reprintOpen}
        onCancel={() => setReprintOpen(false)}
        onRetry={handleRetryPrint}
        retrying={retrying}
      />
      <Drawer
        variant="persistent"
        anchor="right"
        open={open}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  )
}
